import moment from "moment";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import AppointmentService from "../../api/AppointmentService";
import JobService from "../../api/JobService";
import { useAppointment } from "../../hooks/useAppointment";
import { RootState } from "../../store";
import { creatingMonitorActions } from "../../store/monitor-slice";
import { servicedMonitorsActions } from "../../store/serviced-monitors";
import { activeTabsActions } from "../../store/tabs-slice";
import { Document } from "../../types/document";
import { Event } from "../../types/event";
import { MonitorService } from "../../types/monitor";
import { MonitorTypesCard } from "../MonitorTypesCard/MonitorTypesCard";
import { NoData } from "../NoData/NoData";
import { PropertyAreaMonitorsCard } from "../PropertyAreaMonitorsCard/PropertyAreaMonitorsCard";
import PullToRefresh from "../PullToRefresh/PullToRefresh";

interface PropertyAreaMonitorsProps {
  readonly?: boolean;
  monitors: any[];
  isServiceStarted?: boolean;
  startService: () => void;
  isServicesOverview?: boolean;
  jobId: string;
  infestationCountPerArea?: { [key: string]: number };
  eventType?: "infestation" | "noInfestation" | "maintenance" | "unreachable";
  showMapIcon?: boolean;
  isCustomerLookup?: boolean;
  onToggleHidden?: (propertyAreaId: string) => void;
  handleStartService?: () => Promise<void>;
}

type ServicedMonitorsItem = {
  [propertyArea: string]: { [monitorType: string]: number };
};

export const PropertyAreaMonitors = ({
  readonly,
  monitors,
  isServiceStarted,
  jobId,
  startService,
  isServicesOverview,
  infestationCountPerArea,
  eventType,
  showMapIcon,
  isCustomerLookup = false,
  onToggleHidden,
  handleStartService,
}: PropertyAreaMonitorsProps) => {
  const { appointmentId } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { fetchAppointments } = useAppointment(
    {
      "filter[sinceDate]": moment().format("YYYY-MM-DD"),
    },
    true
  );

  const monitorsStep = useSelector(
    (state: RootState) => state.tabs.monitors.step
  );
  const monitorTypes = useSelector(
    (state: RootState) => state.monitor.monitorTypes
  );

  const onPropertyAreaClick = (monitor: any) => {
    if (appointmentId)
      navigate(`/services/${appointmentId}/services-overview/step2`, {
        state: { monitor, readonly },
      });
  };

  const handleNextStep = (
    monitorType: string,
    propertyArea: string,
    map: Document,
    propertyAreaId: string
  ) => {
    dispatch(
      creatingMonitorActions.setMonitorTypes({
        monitorType: monitorType,
        propertyArea: propertyArea,
        map: map,
        propertyAreaId: propertyAreaId,
        monitors: monitors
          .find((monitor: any) => monitor.propertyArea === propertyArea)
          ?.monitors?.filter(
            (monitor: MonitorService) => monitor.monitorType === monitorType
          )
          .sort(
            (
              a: { isTemporary: boolean; position: number },
              b: { isTemporary: boolean; position: number }
            ) => {
              if (a.isTemporary !== b.isTemporary) {
                return a.isTemporary ? 1 : -1;
              }
              return a.position - b.position;
            }
          ),
      })
    );
    dispatch(activeTabsActions.setMonitorsStep(2));
  };

  const fetchServicedMonitors = useCallback(async () => {
    if (!jobId) return;

    try {
      const [eventsResponse, adjustedMonitorsResponse] = await Promise.all([
        JobService.GetGroupedEventsByJob(jobId),
        appointmentId
          ? AppointmentService.GetAdjustedMonitors(appointmentId)
          : Promise.resolve({ data: [] }),
      ]);

      const eventsData = eventsResponse.data;

      const adjustedMonitorIds = new Set(
        adjustedMonitorsResponse.data.flatMap(
          (area: any) =>
            area.monitors?.map((monitor: MonitorService) => monitor.id) ?? []
        )
      );

      const updatedServicedMonitorsCountByPropertyArea: ServicedMonitorsItem =
        {};

      monitors.forEach((monitorData: any) => {
        const {
          propertyArea,
          propertyAreaId,
          monitors: propertyAreaMonitors,
        } = monitorData;

        if (!updatedServicedMonitorsCountByPropertyArea[propertyArea]) {
          updatedServicedMonitorsCountByPropertyArea[propertyArea] = {};

          propertyAreaMonitors.forEach((monitor: MonitorService) => {
            updatedServicedMonitorsCountByPropertyArea[propertyArea][
              monitor.monitorType
            ] = 0;
          });
        }

        const areaEvents = eventsData.find(
          (item: any) => item.propertyAreaId === propertyAreaId
        );

        const allEvents: Event[] = areaEvents
          ? [
              ...(areaEvents.events?.infestation ?? []),
              ...(areaEvents.events?.unreachableOrMissing ?? []),
              ...(areaEvents.events?.maintenancePerformed ?? []),
              ...(areaEvents.events?.noInfestation ?? []),
            ].flat()
          : [];

        propertyAreaMonitors.forEach((monitor: MonitorService) => {
          const monitorEvents = allEvents.filter(
            (event: Event) => event.monitorId === monitor.id
          );

          const hasEvent = monitorEvents.length > 0;
          const isAdjusted = adjustedMonitorIds.has(monitor.id);

          const isServiced = hasEvent || isAdjusted;

          if (isServiced) {
            dispatch(servicedMonitorsActions.setServicedMonitors(monitor.id));

            const propertyTypeCounts =
              updatedServicedMonitorsCountByPropertyArea[propertyArea];

            updatedServicedMonitorsCountByPropertyArea[propertyArea] = {
              ...propertyTypeCounts,
              [monitor.monitorType]:
                (propertyTypeCounts[monitor.monitorType] || 0) + 1,
            };
          }

          // Only monitors with actual events get monitor event information
          if (hasEvent) {
            dispatch(
              servicedMonitorsActions.setMonitorEvents({
                id: monitorEvents[0].id,
                monitorId: monitor.id,
              })
            );
          }
        });
      });

      dispatch(
        servicedMonitorsActions.setServicedMonitorsCount(
          updatedServicedMonitorsCountByPropertyArea
        )
      );
    } catch (error) {
      console.error("Failed to fetch serviced monitors:", error);
    }
  }, [monitors, jobId, appointmentId, dispatch]);

  useEffect(() => {
    fetchServicedMonitors();
  }, [fetchServicedMonitors]);

  const handlePullToRefresh = (callback: Function) => {
    fetchAppointments()
      .then((result) => {
        callback();
      })
      .catch((error) => {
        callback(error);
      });
  };

  return (
    <PullToRefresh onRefresh={handlePullToRefresh}>
      {monitorsStep === 1 ? (
        monitors.length > 0 ? (
          monitors.map((monitor, i: number) => (
            <PropertyAreaMonitorsCard
              key={i}
              dataMonitor={monitor}
              onClick={handleNextStep}
              isServicesOverview={isServicesOverview}
              infestationCount={
                infestationCountPerArea &&
                infestationCountPerArea[monitor.propertyArea]
              }
              onPropertyAreaClick={() => onPropertyAreaClick(monitor)}
              eventType={eventType}
              showMapIcon={showMapIcon}
              isCustomerLookup={isCustomerLookup}
              onToggleHidden={onToggleHidden}
            />
          ))
        ) : (
          <NoData>{t("NoMonitors")}</NoData>
        )
      ) : (
        <MonitorTypesCard
          dataMonitor={monitorTypes}
          startService={startService}
          isServiceStarted={isServiceStarted}
          handleStartService={handleStartService}
        />
      )}
    </PullToRefresh>
  );
};
